import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, FileText, Download, CheckCircle2, AlertCircle,
  History, ChevronRight, Loader2, X, RefreshCw,
} from "lucide-react";
import { businessApi, API } from "../../lib/api";

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";

export default function BusinessImportPage() {
  const [tab, setTab] = React.useState("upload"); // upload | history
  const [uploading, setUploading] = React.useState(false);
  const [batch, setBatch] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [committing, setCommitting] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const loadHistory = React.useCallback(async () => {
    try {
      const { data } = await businessApi().get("/business/shipments/import/history");
      setHistory(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente historik");
    }
  }, []);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleFile = async (file) => {
    if (!file) return;
    const flower = file.name.toLowerCase();
    if (!flower.endsWith(".csv") && !flower.endsWith(".xlsx") && !flower.endsWith(".tsv") && !flower.endsWith(".txt")) {
      toast.error("Kun .csv eller .xlsx filer er understøttet");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Filen er for stor — max 10 MB");
      return;
    }
    setUploading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await businessApi().post("/business/shipments/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(data);
      toast.success(`Preview klar: ${data.valid_rows} gyldige, ${data.invalid_rows} fejlrækker`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const commit = async () => {
    if (!batch?.id) return;
    setCommitting(true);
    try {
      const { data } = await businessApi().post("/business/shipments/import/commit", { batch_id: batch.id });
      toast.success(`${data.created_count} forsendelser oprettet${data.skipped_duplicates_count ? ` (${data.skipped_duplicates_count} duplikater sprunget over)` : ""}`);
      setBatch({ ...batch, status: "committed", created_shipment_ids: data.created_shipment_ids });
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit fejlede");
    } finally {
      setCommitting(false);
    }
  };

  const downloadTemplate = async (ext) => {
    try {
      const res = await businessApi().get(`/business/shipments/import/template.${ext}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ema_import_skabelon.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Skabelon-download fejlede");
    }
  };

  const downloadErrorReport = async () => {
    if (!batch?.id) return;
    try {
      const res = await businessApi().get(`/business/shipments/import/${batch.id}/error-report.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ema_import_errors_${batch.id.slice(0, 8)}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Fejlrapport-download fejlede");
    }
  };

  return (
    <div className="space-y-6" data-testid="business-import-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-brand-red font-bold">CSV / Excel</div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Bulk-import af forsendelser</h1>
        <p className="text-brand-text2 mt-2">Upload op til 1.000 forsendelser ad gangen via CSV eller Excel. Vi validerer hver række, viser dig en preview og lader dig committe kun de gyldige rækker.</p>
      </header>

      <div className="flex gap-1 border-b border-brand">
        <button onClick={() => setTab("upload")} data-testid="biz-import-tab-upload"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === "upload" ? "border-brand-red text-brand-dark" : "border-transparent text-brand-text2 hover:text-brand-dark"}`}>
          <Upload size={14} className="inline mr-1"/> Upload & Preview
        </button>
        <button onClick={() => setTab("history")} data-testid="biz-import-tab-history"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === "history" ? "border-brand-red text-brand-dark" : "border-transparent text-brand-text2 hover:text-brand-dark"}`}>
          <History size={14} className="inline mr-1"/> Historik ({history.length})
        </button>
      </div>

      {tab === "upload" && (
        <div className="space-y-6">
          {/* Templates */}
          <section className="border border-brand bg-white p-5">
            <h2 className="font-display font-bold text-lg text-brand-dark mb-3 flex items-center gap-2">
              <FileText size={18} className="text-brand-red"/> Hent skabelon
            </h2>
            <p className="text-sm text-brand-text2 mb-3">Brug vores skabelon med eksempel-rækker, kolonne-headers og forklaringer. Excel-versionen har en separat &laquo;Vejledning&raquo;-fane.</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => downloadTemplate("csv")} data-testid="biz-import-template-csv"
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-bg2 border border-brand text-sm font-semibold text-brand-dark hover:bg-white transition">
                <Download size={14}/> CSV-skabelon
              </button>
              <button onClick={() => downloadTemplate("xlsx")} data-testid="biz-import-template-xlsx"
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-bg2 border border-brand text-sm font-semibold text-brand-dark hover:bg-white transition">
                <FileSpreadsheet size={14}/> Excel-skabelon (.xlsx)
              </button>
            </div>
          </section>

          {/* Drop zone */}
          {!batch && (
            <section
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="biz-import-dropzone"
              className={`border-2 border-dashed p-12 text-center transition ${dragOver ? "border-brand-red bg-red-50" : "border-brand bg-white"}`}
            >
              <Upload size={48} className="mx-auto text-brand-red mb-4"/>
              <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Træk filen hertil</h3>
              <p className="text-sm text-brand-text2 mb-4">eller</p>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                data-testid="biz-import-choose-file"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                {uploading ? <><Loader2 size={16} className="animate-spin"/> Uploader...</> : <><Upload size={16}/> Vælg fil fra computer</>}
              </button>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.tsv,.txt"
                className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="biz-import-file-input"/>
              <p className="text-xs text-brand-text2 mt-4">Understøttede formater: .csv, .xlsx · Max 10 MB · Max 1.000 rækker</p>
            </section>
          )}

          {/* Preview */}
          {batch && <PreviewPanel batch={batch} onReset={() => setBatch(null)} onCommit={commit} committing={committing} onDownloadErrors={downloadErrorReport}/>}
        </div>
      )}

      {tab === "history" && <HistoryPanel items={history} reload={loadHistory}/>}
    </div>
  );
}

function PreviewPanel({ batch, onReset, onCommit, committing, onDownloadErrors }) {
  const validRows = (batch.rows || []).filter((r) => r.valid);
  const invalidRows = (batch.rows || []).filter((r) => !r.valid);
  const committed = batch.status === "committed";

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-brand p-5">
        <div>
          <div className="font-display font-bold text-lg text-brand-dark">{batch.file_name}</div>
          <div className="text-sm text-brand-text2">{(batch.file_size / 1024).toFixed(1)} KB · {batch.total_rows} rækker · uploadet af {batch.uploaded_by_email}</div>
        </div>
        <div className="flex gap-2">
          {!committed && (
            <button onClick={onReset} className="text-sm text-brand-text2 hover:text-brand-dark inline-flex items-center gap-1" data-testid="biz-import-reset">
              <X size={14}/> Annuller
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total rækker" value={batch.total_rows} testid="biz-import-stat-total"/>
        <StatCard label="Gyldige" value={batch.valid_rows} accent="green" testid="biz-import-stat-valid"/>
        <StatCard label="Fejlrækker" value={batch.invalid_rows} accent={batch.invalid_rows > 0 ? "red" : "gray"} testid="biz-import-stat-invalid"/>
        <StatCard label="Estimeret pris" value={`${fmt(batch.estimated_total_price)} kr`} accent="brand" testid="biz-import-stat-price"/>
      </div>

      {/* Agreement warning */}
      {!batch.agreement_active && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertCircle size={16} className="inline mr-2"/>
          Ingen aktiv B2B-aftale på din konto — du kan ikke committe før EMA aktiverer aftalen. Kontakt <a href="mailto:kontakt@emakurer.dk" className="underline">kontakt@emakurer.dk</a>.
        </div>
      )}

      {/* Commit button */}
      {!committed && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-brand p-4">
          <button onClick={onCommit} disabled={committing || batch.valid_rows === 0 || !batch.agreement_active}
            data-testid="biz-import-commit"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            {committing ? <><Loader2 size={16} className="animate-spin"/> Committer...</> : <><CheckCircle2 size={16}/> Bekræft og opret {batch.valid_rows} forsendelser</>}
          </button>
          {batch.invalid_rows > 0 && (
            <button onClick={onDownloadErrors} data-testid="biz-import-download-errors"
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-bg2 border border-brand text-sm font-semibold text-brand-dark hover:bg-white transition">
              <Download size={14}/> Download fejlrapport (CSV)
            </button>
          )}
          <span className="text-xs text-brand-text2 ml-auto">Kun gyldige rækker committes. Duplikater (samme customer_reference) springes over.</span>
        </div>
      )}

      {committed && (
        <div className="border-l-4 border-green-500 bg-green-50 p-4">
          <div className="flex items-center gap-2 font-bold text-green-900" data-testid="biz-import-committed-banner">
            <CheckCircle2 size={18}/> {batch.created_shipment_ids?.length || 0} forsendelser oprettet
          </div>
          <p className="text-sm text-green-900 mt-1">Se dem under <Link to="/business/shipments" className="underline font-semibold">Forsendelser</Link>. Labels er klar når du henter dem på listen.</p>
        </div>
      )}

      {/* Rows table */}
      <div className="bg-white border border-brand overflow-x-auto" data-testid="biz-import-rows-table">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Modtager</th>
              <th className="px-3 py-2 text-left">By / postnr</th>
              <th className="px-3 py-2 text-left">Service</th>
              <th className="px-3 py-2 text-left">Pickup</th>
              <th className="px-3 py-2 text-right">Vægt</th>
              <th className="px-3 py-2 text-right">Pris</th>
              <th className="px-3 py-2 text-left">Fejl</th>
            </tr>
          </thead>
          <tbody>
            {validRows.map((r) => <RowItem key={r.row_num} r={r}/>)}
            {invalidRows.map((r) => <RowItem key={r.row_num} r={r}/>)}
            {(batch.rows || []).length === 0 && (
              <tr><td colSpan={9} className="text-center text-brand-text2 py-6">Ingen rækker</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatCard({ label, value, accent = "gray", testid }) {
  const colors = {
    gray: "bg-white border-brand text-brand-dark",
    green: "bg-green-50 border-green-300 text-green-900",
    red: "bg-red-50 border-red-300 text-red-900",
    brand: "bg-brand-bg2 border-brand text-brand-dark",
  };
  return (
    <div className={`border p-4 ${colors[accent]}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function RowItem({ r }) {
  const d = r.data || {};
  return (
    <tr className={`border-t border-brand ${r.valid ? "" : "bg-red-50/40"}`} data-testid={`biz-import-row-${r.row_num}`}>
      <td className="px-3 py-2 font-mono text-xs">{r.row_num}</td>
      <td className="px-3 py-2">
        {r.valid ? (
          <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold"><CheckCircle2 size={12}/> OK</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold"><AlertCircle size={12}/> Fejl</span>
        )}
      </td>
      <td className="px-3 py-2">{d.recipient_name || "—"}</td>
      <td className="px-3 py-2 text-xs">{d.delivery_postal_code} {d.delivery_city}</td>
      <td className="px-3 py-2 text-xs">{d.product === "same_day_b2b" ? "Same Day" : d.product === "next_day_b2b" ? "Next Day" : "—"}</td>
      <td className="px-3 py-2 text-xs">{d.pickup_location_name || "—"}</td>
      <td className="px-3 py-2 text-right text-xs">{d.weight_kg ?? "—"} kg</td>
      <td className="px-3 py-2 text-right text-xs">{r.price_estimate ? `${fmt(r.price_estimate)} kr` : "—"}</td>
      <td className="px-3 py-2 text-xs">
        {r.errors?.length > 0 ? (
          <ul className="text-red-700 space-y-1">
            {r.errors.map((e, i) => (
              <li key={i}><b>{e.field}:</b> {e.message}{e.suggested_fix && <span className="text-red-600/80"> — <i>{e.suggested_fix}</i></span>}</li>
            ))}
          </ul>
        ) : "—"}
      </td>
    </tr>
  );
}

function HistoryPanel({ items, reload }) {
  return (
    <section className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-bold text-lg text-brand-dark">Tidligere imports</h2>
        <button onClick={reload} className="text-sm text-brand-text2 hover:text-brand-dark inline-flex items-center gap-1" data-testid="biz-import-history-reload">
          <RefreshCw size={14}/> Opdater
        </button>
      </div>
      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-3 py-2 text-left">Dato</th>
              <th className="px-3 py-2 text-left">Fil</th>
              <th className="px-3 py-2 text-left">Uploadet af</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Oprettet</th>
              <th className="px-3 py-2 text-right">Fejl</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((b) => (
              <tr key={b.id} className="border-t border-brand" data-testid={`biz-import-history-${b.id}`}>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(b.uploaded_at_iso).toLocaleString("da-DK")}</td>
                <td className="px-3 py-2 font-mono text-xs truncate max-w-xs">{b.file_name}</td>
                <td className="px-3 py-2 text-xs">{b.uploaded_by_email}</td>
                <td className="px-3 py-2 text-right">{b.total_rows}</td>
                <td className="px-3 py-2 text-right text-green-700">{(b.created_shipment_ids || []).length}</td>
                <td className="px-3 py-2 text-right text-red-700">{b.invalid_rows}</td>
                <td className="px-3 py-2 text-xs">
                  {b.status === "committed" ? <span className="inline-flex items-center gap-1 text-green-700 font-semibold"><CheckCircle2 size={12}/> Committet</span>
                    : b.status === "preview" ? <span className="text-amber-700 font-semibold">Preview</span>
                    : <span className="text-brand-text2">{b.status}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <a href={`${API}/business/shipments/import/${b.id}/error-report.csv`} target="_blank" rel="noreferrer"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const res = await businessApi().get(`/business/shipments/import/${b.id}/error-report.csv`, { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement("a");
                        a.href = url; a.download = `ema_import_errors_${b.id.slice(0, 8)}.csv`; a.click();
                        window.URL.revokeObjectURL(url);
                      } catch {
                        toast.error("Fejlrapport-download fejlede");
                      }
                    }}
                    className="text-brand-red hover:text-brand-dark inline-flex items-center gap-1 text-xs"
                    data-testid={`biz-import-download-errors-${b.id}`}>
                    <Download size={12}/> Fejl
                  </a>
                </td>
              </tr>
            ))}
            {(items || []).length === 0 && (
              <tr><td colSpan={8} className="text-center text-brand-text2 py-6">Ingen tidligere imports</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
